import { Button } from '@mui/material'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import {
  deleteCertificateRule,
  editCertificateRule,
  getCertificateRuleById,
  getCertificatesRules,
} from 'api/certificates'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import ConfirmPublishModal from 'components/modals/ConfirmPublishModal'
import ConfirmRemoveModal from 'components/modals/ConfirmRemoveModal'
import NotificationSys from 'components/NotificationSystem'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import {
  columnCreatedBy,
  columnUpdatedBy,
  csvColumnCreatedBy,
  csvColumnUpdatedBy,
  getActionColumn,
  RowAction,
} from 'features/UI'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { PublishAction, usePublishModalControl } from 'hooks/usePublishModalControl'
import { useRemoveModalControl } from 'hooks/useRemoveModalControl'
import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import {
  BaseTableRequest,
  CertificateRule,
  CertificateRuleType,
  CertificateRuleTypeLabel,
  FilterModel,
} from 'types'
import { formatDate, getRoute, useGetDataSource } from 'utils'

export function CertificatesAdminPage() {
  const navigate = useNavigate()

  const getRequest = useCallback((params: BaseTableRequest) => {
    const extendFilter: FilterModel = params.filter || {}

    const request = { ...params, filter: extendFilter }

    return getCertificatesRules(request)
  }, [])

  const getDataSource = useGetDataSource(getRequest)
  const { gridApi, columnApi, onGridReady } = useGridControl(getDataSource)

  const handleAdd = useCallback(() => {
    navigate(routes.certificatesAdd)
  }, [navigate])

  const handleEdit = useCallback(
    (id: number) => {
      navigate(getRoute(routes.certificatesEdit, { id }))
    },
    [navigate],
  )

  const publishActionRender = useCallback((props: ICellRendererParams<CertificateRule>) => {
    const isPublished = props?.data?.publishedAt
    return isPublished ? 'Unpublish' : 'Publish'
  }, [])

  const {
    action,
    idToPublish,
    openPublishModal,
    closePublishModal,
    publishLoading,
    handleConfirmPublish,
  } = usePublishModalControl<CertificateRule>({
    successCallback: (_: number, action: PublishAction) => {
      const isPublished = action === PublishAction.Publish
      if (isPublished) {
        NotificationSys.showSuccess(`Certificate is published`)
      } else {
        NotificationSys.showSuccess(`Certificate is unpublished`)
      }
      gridApi?.purgeInfiniteCache()
    },
    loadFunc: getCertificateRuleById,
    updateFunc: (certificate: CertificateRule) => {
      return editCertificateRule({
        id: certificate.id,
        isPublished: certificate.isPublished,
      })
    },
  })

  const onPublish = useCallback(
    async (id: number, props: ICellRendererParams<CertificateRule>) => {
      const isPublished = props?.data?.publishedAt
      const action = isPublished ? PublishAction.Unpublish : PublishAction.Publish
      openPublishModal(id, action)
    },
    [openPublishModal],
  )

  const { idToRemove, openDeleteModal, closeDeleteModal, removeLoading, handleConfirmRemove } =
    useRemoveModalControl({
      deleteFunc: deleteCertificateRule,
      warning: "Can't delete certificate",
      successCallback: () => {
        gridApi?.purgeInfiniteCache()
      },
    })

  const handleDelete = useCallback(
    (id: number) => {
      openDeleteModal(id)
    },
    [openDeleteModal],
  )

  const actions: RowAction[] = useMemo(() => {
    return [
      {
        name: 'Edit',
        onClick: handleEdit,
      },
      { name: 'Delete', onClick: handleDelete },
      { renderName: publishActionRender, onClick: onPublish },
    ]
  }, [handleDelete, handleEdit, onPublish, publishActionRender])

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: 'Certificate Name',
        field: 'name',
        minWidth: 400,
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Published',
        cellRenderer: (props: ICellRendererParams) => {
          if (!props.data) return PLACEHOLDER

          const isPublished = props.data.publishedAt
          return isPublished ? 'Published' : 'Unpublished'
        },
        minWidth: 140,
        colId: 'publishedAt',
      },
      {
        headerName: 'Type',
        minWidth: 140,
        field: 'type',
        cellRenderer: (props: ICellRendererParams) => {
          if (!props.data) return PLACEHOLDER
          return CertificateRuleTypeLabel[props.data.type as CertificateRuleType] || PLACEHOLDER
        },
      },
      {
        headerName: 'Creation Date',
        cellRenderer: columnRenderers.createdAt,
        colId: 'createdAt',
      },
      columnCreatedBy(true),
      {
        headerName: 'Last Edited',
        cellRenderer: columnRenderers.updatedAt,
        colId: 'updatedAt',
      },
      columnUpdatedBy(true),
      { ...getActionColumn(actions) },
    ]
  }, [actions])

  const csvFields = useMemo(() => {
    return [
      { label: 'Certificate Name', value: 'name' },
      {
        label: 'Published',
        value: ({ isPublished }: CertificateRule) => (isPublished ? 'Published' : 'Unpublished'),
      },
      {
        label: 'Type',
        value: ({ type }: CertificateRule) => {
          return CertificateRuleTypeLabel[type] || PLACEHOLDER
        },
      },
      {
        label: 'Creation Date',
        value: ({ createdAt }: CertificateRule) => {
          if (!createdAt) return PLACEHOLDER
          return formatDate(createdAt)
        },
      },
      csvColumnCreatedBy(),
      {
        label: 'Last Edited',
        value: ({ updatedAt }: CertificateRule) => {
          if (!updatedAt) return PLACEHOLDER
          return formatDate(updatedAt)
        },
      },
      csvColumnUpdatedBy(),
    ]
  }, [])

  const { csvLoading, onExportCSV } = useExportCSV(
    getRequest,
    gridApi,
    columnApi,
    csvFields,
    'Certificates.csv',
  )

  return (
    <>
      <TableToolbar
        title="Certificates Overview"
        exportCSVEnable
        csvLoading={csvLoading}
        onExportCSV={onExportCSV}
      >
        <Button variant="outlined" onClick={handleAdd}>
          Add Certificate
        </Button>
      </TableToolbar>

      <InfinityAgGrid pagination columnDefs={columnDefs} onGridReady={onGridReady} />

      <ConfirmRemoveModal
        entityToRemove="Certificate"
        loading={removeLoading}
        isOpen={idToRemove !== null}
        handleConfirm={handleConfirmRemove}
        handleClose={closeDeleteModal}
      />

      <ConfirmPublishModal
        loading={publishLoading}
        handleClose={closePublishModal}
        isOpen={idToPublish !== null}
        handleConfirm={handleConfirmPublish}
        action={action}
        entity="Certificate"
      />
    </>
  )
}
